import React, { useMemo, useState } from "react";
import { Phone, CalendarDays, Clock4 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import Reveal from "@/components/anim/Reveal";

const CallScheduler = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    slot: "morning",
    topic: "",
  });
  const [loading, setLoading] = useState(false);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  }, []);

  const onChange = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/call-requests", form);
      toast.success(t.call.success);
      setForm({ name: "", phone: "", email: "", date: "", slot: "morning", topic: "" });
    } catch {
      toast.error(t.call.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-brand-ink text-white section-y relative overflow-hidden" data-testid="call-section">
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-brand-deep/30 blur-3xl pointer-events-none animate-float-slow" />
      <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <p className="overline !text-brand-sky">{t.call.eyebrow}</p>
          <h2 className="mt-4 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            {t.call.title}
          </h2>
          <p className="mt-6 text-lg text-white/70 leading-relaxed">{t.call.subtitle}</p>

          <div className="mt-10 space-y-4 text-sm text-white/80">
            <div className="flex items-center gap-3"><Phone size={16} className="text-brand-sky" />30 min · gratuit · sans engagement</div>
            <div className="flex items-center gap-3"><CalendarDays size={16} className="text-brand-sky" />Réponse sous 24 h ouvrées</div>
            <div className="flex items-center gap-3"><Clock4 size={16} className="text-brand-sky" />{t.contact.info_hours}</div>
          </div>
        </Reveal>

        <Reveal as="form" delay={120}
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-white text-brand-ink p-8 sm:p-10 space-y-5 transition-shadow duration-500 hover:shadow-2xl"
          data-testid="call-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="call-name" className="text-xs font-semibold tracking-[0.12em] uppercase">{t.call.name} *</Label>
              <Input id="call-name" required value={form.name} onChange={onChange("name")} className="rounded-none border-brand-border h-12 focus-visible:ring-brand-deep" data-testid="call-input-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="call-phone" className="text-xs font-semibold tracking-[0.12em] uppercase">{t.call.phone} *</Label>
              <Input id="call-phone" required type="tel" value={form.phone} onChange={onChange("phone")} className="rounded-none border-brand-border h-12 focus-visible:ring-brand-deep" data-testid="call-input-phone" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="call-email" className="text-xs font-semibold tracking-[0.12em] uppercase">{t.call.email} *</Label>
            <Input id="call-email" required type="email" value={form.email} onChange={onChange("email")} className="rounded-none border-brand-border h-12 focus-visible:ring-brand-deep" data-testid="call-input-email" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="call-date" className="text-xs font-semibold tracking-[0.12em] uppercase">{t.call.date} *</Label>
              <Input id="call-date" required type="date" min={minDate} value={form.date} onChange={onChange("date")} className="rounded-none border-brand-border h-12 focus-visible:ring-brand-deep" data-testid="call-input-date" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="call-slot" className="text-xs font-semibold tracking-[0.12em] uppercase">{t.call.time} *</Label>
              <Select value={form.slot} onValueChange={onChange("slot")}>
                <SelectTrigger id="call-slot" className="rounded-none border-brand-border h-12 focus:ring-brand-deep" data-testid="call-input-slot">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">{t.call.slot_morning}</SelectItem>
                  <SelectItem value="afternoon">{t.call.slot_afternoon}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="call-topic" className="text-xs font-semibold tracking-[0.12em] uppercase">{t.call.topic}</Label>
            <Input id="call-topic" value={form.topic} onChange={onChange("topic")} className="rounded-none border-brand-border h-12 focus-visible:ring-brand-deep" data-testid="call-input-topic" />
          </div>

          <Button type="submit" disabled={loading} className="rounded-none bg-brand-ink hover:bg-brand-deep text-white h-12 px-7 text-sm font-semibold tracking-wide w-full sm:w-auto group transition-all duration-300 hover:px-9" data-testid="call-submit">
            {loading ? t.call.submitting : <>{t.call.submit}<Phone size={16} className="ml-2 transition-transform duration-300 group-hover:rotate-12" /></>}
          </Button>
        </Reveal>
      </div>
    </section>
  );
};

export default CallScheduler;
